import { useState } from 'react';
import { useProduct, useNotifications } from '../contexts';
import { trackError, ErrorSeverity, type ErrorContext } from '../utils/error-tracking';
import {
	severityForOwnFetchFailure,
	transportFailureContext,
} from '../utils/fetch-transport-failure';
import { NsfwListingStateSuccessResponseSchema } from '../schemas/api/listings/nsfw';

/**
 * File a failed admin action against one of our own listing routes. A bare
 * "Failed to fetch" here is the request never being answered -- an extension's
 * fetch wrapper cutting it, the tab closing, the network dropping -- and the
 * row needs the browser state at the catch to tell that from the route being
 * down. A request the page itself abandoned files at info; anything else stays
 * an error, because the admin's action was lost.
 */
const trackListingActionFailure = (error: unknown, action: string, component: string) => {
	const transport = transportFailureContext(error);
	const context: ErrorContext = {
		action,
		component,
		extra: { ...transport },
	};
	trackError(
		error instanceof Error ? error : new Error(String(error)),
		severityForOwnFetchFailure(error, ErrorSeverity.ERROR, transport),
		context
	);
};

/** What just succeeded, for callers that want to say so. */
export type ListingAdminAction = 'feature' | 'partner' | 'archive' | 'nsfw';

export interface UseListingThumbApiOptions {
	/**
	 * Called after an action the server accepted. A grid tile needs nothing --
	 * the card leaves the list, which is the feedback. The product page keeps
	 * showing the listing, so it says so in a notification instead. Failures
	 * never reach here; they are notified inside the hook.
	 */
	onSuccess?: (
		action: ListingAdminAction,
		detail?: { featured?: boolean; partnerName?: string; nsfw?: boolean },
	) => void;
	/** Which surface the action was fired from, for error rows. */
	component?: string;
}

interface UseListingThumbApiResult {
	isFeatureLoading: boolean;
	isPartnerLoading: boolean;
	isArchiveLoading: boolean;
	handleFeature: (listingId: number, shouldFeature: boolean) => Promise<void>;
	handleFeatureForPartner: (listingId: number, membershipType: number, partnerName: string) => Promise<void>;
	handleArchive: (listingId: number) => Promise<void>;
	isNsfwLoading: boolean;
	/** true hides it from rows and browse; false puts back one Jev or an admin hid. */
	handleNsfw: (listingId: number, nsfw?: boolean) => Promise<void>;
	/** Ask whether it is hidden now; the menu calls this when it opens. */
	loadNsfwState: (listingId: number) => Promise<void>;
	/** Whether it is hidden, as last loaded or set; null when not known. */
	nsfwFor: (listingId: number) => boolean | null;
}

export const useListingThumbApi = (
	options: UseListingThumbApiOptions = {},
): UseListingThumbApiResult => {
	const { onSuccess, component = 'ListingThumb' } = options;
	const { currentList, replaceCurrentList } = useProduct();
	const { createNotification } = useNotifications();
	const [isFeatureLoading, setIsFeatureLoading] = useState(false);
	const [isPartnerLoading, setIsPartnerLoading] = useState(false);
	const [isArchiveLoading, setIsArchiveLoading] = useState(false);
	const [isNsfwLoading, setIsNsfwLoading] = useState(false);
	const [nsfwState, setNsfwState] = useState<{ listingId: number; nsfw: boolean } | null>(null);

	const removeListingFromList = (listingId: number) => {
		const updatedList = currentList.filter(listing => listing.id !== listingId.toString());
		replaceCurrentList(updatedList);
	};

	const handleFeature = async (listingId: number, shouldFeature: boolean) => {
		setIsFeatureLoading(true);
		try {
			const response = await fetch(`/api/listings/${listingId}/feature`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ feature: shouldFeature }),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.error || (shouldFeature ? 'Failed to feature listing' : 'Failed to unfeature listing'));
			}

			if (data.featured !== shouldFeature) {
				throw new Error(shouldFeature ? 'Listing was not featured' : 'Listing was not unfeatured');
			}

			removeListingFromList(listingId);
			onSuccess?.('feature', { featured: shouldFeature });

		} catch (error) {
			trackListingActionFailure(error, 'feature-listing', component);
			const errorMessage = error instanceof Error ? error.message : (shouldFeature ? 'Failed to feature listing' : 'Failed to unfeature listing');
			createNotification({
				type: 'error',
				msg: errorMessage,
				dismissed: false,
			});
		} finally {
			setIsFeatureLoading(false);
		}
	};

	const handleFeatureForPartner = async (listingId: number, membershipType: number, partnerName: string) => {
		setIsPartnerLoading(true);
		try {
			const response = await fetch(`/api/listings/${listingId}/feature-for-partner`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ membershipType }),
			});

			if (!response.ok) {
				throw new Error('Failed to feature listing for partner');
			}

			removeListingFromList(listingId);
			onSuccess?.('partner', { partnerName });

		} catch (error) {
			trackListingActionFailure(error, 'feature-listing-for-partner', component);
			createNotification({
				type: 'error',
				msg: `Failed to feature listing for ${partnerName}. Please try again.`,
				dismissed: false,
			});
		} finally {
			setIsPartnerLoading(false);
		}
	};

	const handleArchive = async (listingId: number) => {
		setIsArchiveLoading(true);
		try {
			const response = await fetch(`/api/listings/${listingId}/archive`, {
				method: 'POST',
			});

			if (!response.ok) {
				throw new Error('Failed to archive listing');
			}

			removeListingFromList(listingId);
			onSuccess?.('archive');

		} catch (error) {
			trackListingActionFailure(error, 'archive-listing', component);
			createNotification({
				type: 'error',
				msg: 'Failed to archive listing. Please try again.',
				dismissed: false,
			});
		} finally {
			setIsArchiveLoading(false);
		}
	};

	/**
	 * Mark it NSFW: still live and findable by typed search, but out of every
	 * row and browse page (pages/api/listings/[id]/nsfw.ts). So it leaves the
	 * grid it was right-clicked in, as an archive does. `false` puts back a
	 * listing Jev or an admin hid; that one stays where it is.
	 */
	const handleNsfw = async (listingId: number, nsfw = true) => {
		setIsNsfwLoading(true);
		try {
			const response = await fetch(`/api/listings/${listingId}/nsfw`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ nsfw }),
			});

			if (!response.ok) {
				throw new Error(`Failed to mark listing ${nsfw ? 'NSFW' : 'not NSFW'}: HTTP ${response.status}`);
			}

			if (nsfw) removeListingFromList(listingId);
			setNsfwState({ listingId, nsfw });
			onSuccess?.('nsfw', { nsfw });
		} catch (error) {
			trackListingActionFailure(error, 'nsfw-listing', component);
			createNotification({
				type: 'error',
				msg: `Failed to mark listing ${nsfw ? 'NSFW' : 'not NSFW'}. Please try again.`,
				dismissed: false,
			});
		} finally {
			setIsNsfwLoading(false);
		}
	};

	/**
	 * Whether Jev or an admin already hid it, so the menu can offer "Not NSFW".
	 * A failed read leaves it unknown and the menu offers "NSFW", as before.
	 * No loading flag: the other buttons stay usable while it is asked.
	 */
	const loadNsfwState = async (listingId: number) => {
		setNsfwState(null);
		try {
			const response = await fetch(`/api/listings/${listingId}/nsfw`);
			if (!response.ok) throw new Error(`Failed to read NSFW state: HTTP ${response.status}`);
			// safeParse: a deploy-skewed shape reads as unknown, never a throw.
			const parsed = NsfwListingStateSuccessResponseSchema.safeParse(await response.json());
			setNsfwState(parsed.success ? { listingId, nsfw: parsed.data.nsfw } : null);
		} catch (error) {
			setNsfwState(null);
			trackListingActionFailure(error, 'nsfw-state', component);
		}
	};

	const nsfwFor = (listingId: number): boolean | null =>
		nsfwState?.listingId === listingId ? nsfwState.nsfw : null;

	return {
		isFeatureLoading,
		isPartnerLoading,
		isArchiveLoading,
		handleFeature,
		handleFeatureForPartner,
		handleArchive,
		isNsfwLoading,
		handleNsfw,
		loadNsfwState,
		nsfwFor,
	};
};
